import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Grid,
  Header,
  Input,
  List,
  Modal,
  Segment,
  Select,
  TextArea,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import {
  addEmailToDb,
  addHistoryToDb,
  buildSenderSignature,
  buildSharingEmailHtml,
  sendCustomDocSharingEmail,
  setPendingNotificationsInDb,
} from "../../../app/firestore/firestoreService";
import { convertAddressStreetAndUnit } from "../../../app/common/util/util";
import { getDefaultRecipientRoleGroup } from "../../emailTemplates/emailTemplateDefaults";

function actionLabel(action) {
  return action === "signing" ? "Signature requested" : "Shared to view";
}

export default function DocPendingNotificationModal({ group, notifications }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const { transaction, allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { emailTemplates } = useSelector((state) => state.emailTemplates);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const agentProfile = transaction.agentProfile?.lastName
    ? transaction.agentProfile
    : currentUserProfile;

  // The sender is always the logged-in user (so TC/manager/assistant send from themselves).
  const sender = currentUserProfile;

  // Recipients are every party in this role group that has an email.
  const recipientParties = (allParties || []).filter(
    (party) =>
      party.email && getDefaultRecipientRoleGroup(party.role) === group
  );
  const recipientEmails = new Set(recipientParties.map((p) => p.email));

  // ── Team members available for CC/BCC ──────────────────────────────────────
  const agentEmail = transaction.agentProfile?.email || "";
  const tcEmail = transaction.transactionCoordinator?.email || "";
  const agentAsTcEmail = transaction.agentAsTransactionCoordinator?.email || "";
  const hasTc = transaction.hasTc || transaction.hasAgentAsTc;
  const effectiveTcEmail = tcEmail || agentAsTcEmail;
  const tcInfo =
    transaction.transactionCoordinator ||
    transaction.agentAsTransactionCoordinator ||
    {};

  const coAgentEmail = transaction.coAgent?.email || "";
  const hasCoAgent = !!transaction.coAgentId && !!coAgentEmail;

  const managerEmail = transaction.managerDetails?.managerEmail || "";
  const hasManager = !!transaction.managerId && !!managerEmail;

  const assistants = Array.isArray(transaction.assistant)
    ? transaction.assistant
    : [];
  const managerAssistants = Array.isArray(
    transaction.agentProfile?.managerAssistants
  )
    ? transaction.agentProfile.managerAssistants
    : [];

  // ── CC/BCC checkbox state ──────────────────────────────────────────────────
  const [ccAgent, setCcAgent] = useState(false);
  const [bccAgent, setBccAgent] = useState(false);
  const [ccCoAgent, setCcCoAgent] = useState(false);
  const [bccCoAgent, setBccCoAgent] = useState(false);
  const [ccTc, setCcTc] = useState(false);
  const [bccTc, setBccTc] = useState(false);
  const [ccManager, setCcManager] = useState(false);
  const [bccManager, setBccManager] = useState(false);

  const [ccAssistants, setCcAssistants] = useState(() =>
    Object.fromEntries(assistants.filter((a) => a.email).map((a) => [a.email, false]))
  );
  const [bccAssistants, setBccAssistants] = useState(() =>
    Object.fromEntries(assistants.filter((a) => a.email).map((a) => [a.email, false]))
  );
  const [ccManagerAssistants, setCcManagerAssistants] = useState(() =>
    Object.fromEntries(managerAssistants.filter((ma) => ma.email).map((ma) => [ma.email, false]))
  );
  const [bccManagerAssistants, setBccManagerAssistants] = useState(() =>
    Object.fromEntries(managerAssistants.filter((ma) => ma.email).map((ma) => [ma.email, false]))
  );

  const street = transaction?.address?.street;
  // ${sender?.lastName || ""} has u
  const defaultSubject =
    `Updates for ` +
    (street ? ` ${street}` : transaction?.title || "");

  const changesList = notifications
    .map((n) => `- ${n.docName} (${actionLabel(n.action)})`)
    .join("\n");
  // Signs off as "Thanks, {sender}" normally, or "Sent by {sender} [role]" plus
  // the agent/coagent on record when the sender isn't the transaction's primary agent.
  const signature = buildSenderSignature(sender, transaction);
  const defaultMessage = `Hi there,

There are updates for you in your portal on the following documents:
${changesList}

Please log in to review them. Let me know if you have any questions.${signature.text}`;

  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);

  const templateOptions = (emailTemplates || []).map((template) => ({
    key: template.id,
    value: template.id,
    text: template.name || template.title || "Untitled template",
  }));

  function handleApplyTemplate(templateId) {
    const template = (emailTemplates || []).find((t) => t.id === templateId);
    if (!template) return;
    if (template.subject?.trim()) setSubject(template.subject.trim());
    if (template.message?.trim()) setMessage(template.message.trim());
  }

  function handleCancel() {
    dispatch(closeModal({ modalType: "DocPendingNotificationModal" }));
  }

  async function handleSend() {
    if (recipientParties.length === 0) {
      toast.error(`No ${group} with an email address to notify.`);
      return;
    }
    setIsSubmitting(true);
    try {
      // ── Build CC/BCC lists ────────────────────────────────────────────────
      const additionalCcEmails = [];
      const additionalBccEmails = [];
      const ccEmailsAndRoles = [];
      const bccEmailsAndRoles = [];

      function addCc(email, role, firstName, lastName) {
        if (!email || recipientEmails.has(email)) return;
        additionalCcEmails.push(email);
        ccEmailsAndRoles.push({ role, email, firstName: firstName || "", lastName: lastName || "" });
      }
      function addBcc(email, role, firstName, lastName) {
        if (!email || recipientEmails.has(email)) return;
        additionalBccEmails.push(email);
        bccEmailsAndRoles.push({ role, email, firstName: firstName || "", lastName: lastName || "" });
      }

      if (ccAgent) addCc(agentEmail, "Agent", agentProfile?.firstName, agentProfile?.lastName);
      if (bccAgent) addBcc(agentEmail, "Agent", agentProfile?.firstName, agentProfile?.lastName);

      if (ccCoAgent) addCc(coAgentEmail, "Co-Agent", transaction.coAgent?.firstName, transaction.coAgent?.lastName);
      if (bccCoAgent) addBcc(coAgentEmail, "Co-Agent", transaction.coAgent?.firstName, transaction.coAgent?.lastName);

      if (ccTc) addCc(effectiveTcEmail, "TC", tcInfo.firstName, tcInfo.lastName);
      if (bccTc) addBcc(effectiveTcEmail, "TC", tcInfo.firstName, tcInfo.lastName);

      if (ccManager) addCc(managerEmail, "Manager", transaction.managerDetails?.firstName, transaction.managerDetails?.lastName);
      if (bccManager) addBcc(managerEmail, "Manager", transaction.managerDetails?.firstName, transaction.managerDetails?.lastName);

      assistants.forEach((a) => {
        if (!a.email) return;
        if (ccAssistants[a.email]) addCc(a.email, "Assistant", a.firstName, a.lastName);
        if (bccAssistants[a.email]) addBcc(a.email, "Assistant", a.firstName, a.lastName);
      });
      managerAssistants.forEach((ma) => {
        if (!ma.email) return;
        if (ccManagerAssistants[ma.email]) addCc(ma.email, "Manager Assistant", ma.firstName, ma.lastName);
        if (bccManagerAssistants[ma.email]) addBcc(ma.email, "Manager Assistant", ma.firstName, ma.lastName);
      });

      // ── Send email (sender = logged-in user) ─────────────────────────────
      await sendCustomDocSharingEmail(
        recipientParties,
        "sharing",
        sender,
        transaction,
        subject,
        message,
        additionalCcEmails,
        additionalBccEmails
      );

      // ── Record in Email History (not env-gated) ───────────────────────────
      const toEmailsAndRoles = recipientParties.map((party) => ({
        role: party.role,
        email: party.email,
        firstName: party.firstName || "",
        lastName: party.lastName || "",
      }));
      // Rebuild the same branded wrapper (logo, transaction address, brokerage
      // logo) the actual email used, so the log shows it too. The login link
      // here is generic (not a recipient-personalized deep link) since this is
      // a display/reference copy.
      const urlApp =
        process.env.REACT_APP_ENVIRONMENT === "development"
          ? "http://localhost:3000"
          : "https://app.transactioner.com";
      const transactionAddress = transaction?.address
        ? convertAddressStreetAndUnit(transaction.address)
        : "";
      const brokerageLogoRef =
        transaction.agentProfile?.brokerageLogoRef?.length > 0
          ? "<p><img height='70px' src='" +
            transaction.agentProfile.brokerageLogoRef +
            "'/></p>"
          : "";
      const messageHtml = buildSharingEmailHtml({
        subject,
        bodyHtml: message.replace(/\n/g, "<br/>"),
        loginUrl: urlApp,
        transactionAddress,
        brokerageLogoRef,
      });
      await addEmailToDb(
        {
          transactionId: transaction.id,
          subject,
          message,
          from: `${sender?.firstName || ""} ${sender?.lastName || ""}`,
          toEmails: recipientParties.map((party) => party.email),
          toEmailsAndRoles,
          ccEmails: additionalCcEmails,
          ccEmailsAndRoles,
          bccEmails: additionalBccEmails,
          bccEmailsAndRoles,
          otherEmailsTo: "",
          otherEmailsCc: "",
          otherEmailsBcc: "",
          type: "queued_group_notification",
          documentName: notifications.map((n) => n.docName).join(", "),
          recipientRole: group,
          recipientName: group,
          isCustomEmail: true,
        },
        { message: { subject, text: message, html: messageHtml } }
      );

      // ── Activity history ──────────────────────────────────────────────────
      recipientParties.forEach((party) => {
        notifications.forEach((notification) => {
          addHistoryToDb(
            transaction.id,
            currentUserProfile,
            "notified",
            notification.docName,
            party
          );
        });
      });

      // ── Clear sent notifications from queue ───────────────────────────────
      const sentIds = notifications.map((n) => n.id);
      const remaining = (transaction.pendingNotifications || []).filter(
        (n) => !sentIds.includes(n.id)
      );
      await setPendingNotificationsInDb(transaction.id, remaining);

      toast.success(`Notification sent to ${group}.`);
      dispatch(closeModal({ modalType: "DocPendingNotificationModal" }));
    } catch (error) {
      console.error("Error sending pending notification:", error);
      toast.error("Failed to send notification. Please try again.");
      setIsSubmitting(false);
    }
  }

  return (
    <ModalWrapper size="large">
      <Segment clearing>
        <div
          className={
            isMobile
              ? "zero horizontal margin small top margin"
              : "medium horizontal margin small top margin"
          }
        >
          <Header icon="mail" content={`Send Notification to ${group}`} />
          <Modal.Content>{renderContent()}</Modal.Content>
          <Modal.Actions>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleSend}
              loading={isSubmitting}
              disabled={isSubmitting || !subject.trim() || !message.trim()}
            >
              Send Email
            </Button>
          </Modal.Actions>
        </div>
      </Segment>
    </ModalWrapper>
  );

  function renderContent() {
    return (
      <>
        <p>
          <strong>To:</strong>{" "}
          {recipientParties.length > 0
            ? recipientParties
                .map((p) => `${p.firstName || ""} ${p.lastName || ""} (${p.email})`)
                .join(", ")
            : `No ${group} with an email address`}
        </p>
        <strong>Changes:</strong>
        <List bulleted className="mini top margin small bottom margin">
          {notifications.map((notification) => (
            <List.Item key={notification.id}>
              {notification.docName} — {actionLabel(notification.action)}
              {notification.role !== group ? ` (${notification.role})` : ""}
            </List.Item>
          ))}
        </List>
        <Form>
          {templateOptions.length > 0 && (
            <Form.Field>
              <label>
                <strong>Apply an email template:</strong>
              </label>
              <Select
                placeholder="Select a template"
                options={templateOptions}
                onChange={(_, { value }) => handleApplyTemplate(value)}
                fluid
                selection
              />
            </Form.Field>
          )}
          <Form.Field>
            <label>
              <strong>Subject Line:</strong>
            </label>
            <Input
              fluid
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </Form.Field>
          <Form.Field>
            <label>
              <strong>Message:</strong>
            </label>
            <TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Email message"
              rows={11}
            />
          </Form.Field>

          {/* ── CC / BCC ──────────────────────────────────────────────── */}
          <Divider />
          <Form.Field>
            <label>
              <strong>Copy Options:</strong>
            </label>
            <Grid stackable style={{ marginTop: 6 }}>
              <Grid.Row>
                <Grid.Column width={8}>
                  {/* Agent */}
                  {agentEmail && (
                    <div style={{ marginBottom: 8 }}>
                      <Checkbox label="CC" checked={ccAgent}
                        onChange={(_, { checked }) => { setCcAgent(checked); if (checked) setBccAgent(false); }}
                        style={{ marginRight: 12 }} />
                      <Checkbox label="BCC" checked={bccAgent}
                        onChange={(_, { checked }) => { setBccAgent(checked); if (checked) setCcAgent(false); }} />
                      <span style={{ marginLeft: 10, fontWeight: 500 }}>
                        Agent ({agentProfile?.firstName} {agentProfile?.lastName})
                      </span>
                    </div>
                  )}
                  {/* Co-Agent */}
                  {hasCoAgent && (
                    <div style={{ marginBottom: 8 }}>
                      <Checkbox label="CC" checked={ccCoAgent}
                        onChange={(_, { checked }) => { setCcCoAgent(checked); if (checked) setBccCoAgent(false); }}
                        style={{ marginRight: 12 }} />
                      <Checkbox label="BCC" checked={bccCoAgent}
                        onChange={(_, { checked }) => { setBccCoAgent(checked); if (checked) setCcCoAgent(false); }} />
                      <span style={{ marginLeft: 10, fontWeight: 500 }}>
                        Co-Agent ({transaction.coAgent?.firstName} {transaction.coAgent?.lastName})
                      </span>
                    </div>
                  )}
                  {/* TC */}
                  {hasTc && effectiveTcEmail && (
                    <div style={{ marginBottom: 8 }}>
                      <Checkbox label="CC" checked={ccTc}
                        onChange={(_, { checked }) => { setCcTc(checked); if (checked) setBccTc(false); }}
                        style={{ marginRight: 12 }} />
                      <Checkbox label="BCC" checked={bccTc}
                        onChange={(_, { checked }) => { setBccTc(checked); if (checked) setCcTc(false); }} />
                      <span style={{ marginLeft: 10, fontWeight: 500 }}>
                        TC ({tcInfo.firstName} {tcInfo.lastName})
                      </span>
                    </div>
                  )}
                  {/* Manager */}
                  {hasManager && (
                    <div style={{ marginBottom: 8 }}>
                      <Checkbox label="CC" checked={ccManager}
                        onChange={(_, { checked }) => { setCcManager(checked); if (checked) setBccManager(false); }}
                        style={{ marginRight: 12 }} />
                      <Checkbox label="BCC" checked={bccManager}
                        onChange={(_, { checked }) => { setBccManager(checked); if (checked) setCcManager(false); }} />
                      <span style={{ marginLeft: 10, fontWeight: 500 }}>
                        Manager ({transaction.managerDetails?.firstName} {transaction.managerDetails?.lastName})
                      </span>
                    </div>
                  )}
                  {/* Assistants */}
                  {assistants.filter((a, i, arr) =>
                    a.email && !recipientEmails.has(a.email) &&
                    arr.findIndex((x) => x.email === a.email) === i
                  ).map((a) => (
                    <div key={a.email} style={{ marginBottom: 8 }}>
                      <Checkbox label="CC" checked={ccAssistants[a.email] || false}
                        onChange={(_, { checked }) => { setCcAssistants((p) => ({ ...p, [a.email]: checked })); if (checked) setBccAssistants((p) => ({ ...p, [a.email]: false })); }}
                        style={{ marginRight: 12 }} />
                      <Checkbox label="BCC" checked={bccAssistants[a.email] || false}
                        onChange={(_, { checked }) => { setBccAssistants((p) => ({ ...p, [a.email]: checked })); if (checked) setCcAssistants((p) => ({ ...p, [a.email]: false })); }} />
                      <span style={{ marginLeft: 10, fontWeight: 500 }}>
                        Assistant ({a.firstName} {a.lastName})
                      </span>
                    </div>
                  ))}
                </Grid.Column>

                <Grid.Column width={8}>
                  {/* Manager Assistants */}
                  {managerAssistants.filter((ma, i, arr) =>
                    ma.email && !recipientEmails.has(ma.email) &&
                    arr.findIndex((x) => x.email === ma.email) === i
                  ).map((ma) => (
                    <div key={ma.email} style={{ marginBottom: 8 }}>
                      <Checkbox label="CC" checked={ccManagerAssistants[ma.email] || false}
                        onChange={(_, { checked }) => { setCcManagerAssistants((p) => ({ ...p, [ma.email]: checked })); if (checked) setBccManagerAssistants((p) => ({ ...p, [ma.email]: false })); }}
                        style={{ marginRight: 12 }} />
                      <Checkbox label="BCC" checked={bccManagerAssistants[ma.email] || false}
                        onChange={(_, { checked }) => { setBccManagerAssistants((p) => ({ ...p, [ma.email]: checked })); if (checked) setCcManagerAssistants((p) => ({ ...p, [ma.email]: false })); }} />
                      <span style={{ marginLeft: 10, fontWeight: 500 }}>
                        {ma.title || "Manager Assistant"} ({ma.firstName} {ma.lastName})
                      </span>
                    </div>
                  ))}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form.Field>
        </Form>
      </>
    );
  }
}
